<!-- FT-216 REQ-10 п.2: карточка открытой рекомендации — заголовок (группа, предложение, даты), обоснование
     словами из чисел сигналов, раскрытие «машины со старой и новой ценой» (текущие значения, REQ-08), кнопки
     «Применить», «Отклонить», «Не предлагать по этой группе». Сама запросов не шлёт — события ловит виджет. -->
<template>
  <v-card outlined class="mb-2" :data-testid="`dp-rec-${rec.id}`">
    <v-card-text class="pa-3">
      <div class="text-body-2 font-weight-medium" data-testid="dp-rec-group">{{ groupLabel }}</div>
      <div class="text-body-1" data-testid="dp-rec-proposal">{{ proposal }}</div>
      <div v-if="reason" class="text-caption text--secondary mt-1" data-testid="dp-rec-reason">{{ reason }}</div>

      <v-btn v-if="rec.cars.length" x-small text class="px-0 mt-1" data-testid="dp-rec-cars-toggle" @click="expanded = !expanded">
        {{ $t("dynamic_pricing.rec.cars_n", { n: rec.cars.length }) }}
        <v-icon small>{{ expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </v-btn>
      <v-list v-if="expanded" dense class="py-0" data-testid="dp-rec-cars">
        <v-list-item v-for="car in rec.cars" :key="car.id" class="px-0" style="min-height: 28px" data-testid="dp-rec-car">
          <v-list-item-content class="py-1">
            <!-- Исключённая сейчас машина (excluded, new_price: null) — старая цена и пометка, без «→ —»;
                 нет ни одной цены — только код и пометка. Поправка на несколько дней машины — строка на каждый
                 день: применение решает по дням, сводка машины их различия скрыла бы. -->
            <div class="d-flex flex-wrap justify-space-between align-center text-body-2" style="gap: 4px 12px">
              <span>{{ car.code }}</span>
              <span v-if="!perDay(car)" class="d-flex align-center flex-wrap" style="gap: 4px 8px">
                <v-chip v-if="car.excluded" x-small label data-testid="dp-rec-car-excluded">
                  {{ $t("dynamic_pricing.rec.car_excluded") }}
                </v-chip>
                <span v-if="priceText(car)" class="text-no-wrap" data-testid="dp-rec-car-price">{{ priceText(car) }}</span>
              </span>
            </div>
            <template v-if="perDay(car)">
              <div v-for="day in car.days" :key="day.date" class="text-caption text--secondary" data-testid="dp-rec-car-day">
                {{ dayText(day) }}
              </div>
            </template>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card-text>
    <v-card-actions class="pt-0 px-3 pb-3 d-flex flex-wrap" style="gap: 4px">
      <v-btn small color="primary" :disabled="busy" data-testid="dp-rec-apply" @click="$emit('apply', rec)">
        {{ $t("dynamic_pricing.rec.apply") }}
      </v-btn>
      <v-btn small text :disabled="busy" data-testid="dp-rec-reject" @click="$emit('reject', rec)">
        {{ $t("dynamic_pricing.rec.reject") }}
      </v-btn>
      <!-- Длинная подпись (el/de в верхнем регистре) в карточке ~330 px на 375 px — переносом, не за край -->
      <v-btn small text :disabled="busy" class="dp-rec__wrap-btn" data-testid="dp-rec-suppress" @click="$emit('suppress', rec)">
        {{ $t("dynamic_pricing.rec.suppress") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { formatMoney } from "@/components/dashboard/rent_to_own/rentToOwnFormat";
import { priceGroupLabelOptions } from "@/utils/priceGroupLabel";
import { formatShortDate, recommendationGroupLabel, recommendationProposal, recommendationReason } from "@/utils/dynamicPricingFormat";

export default {
  name: "RecommendationCard",
  props: {
    rec: { type: Object, required: true },
    unit: { type: String, default: null },
    busy: { type: Boolean, default: false },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    labelOptions() {
      return priceGroupLabelOptions(this, this.unit);
    },
    groupLabel() {
      return recommendationGroupLabel(this.rec, this.labelOptions);
    },
    proposal() {
      return recommendationProposal(this.rec, this.$t.bind(this));
    },
    reason() {
      return recommendationReason(this.rec, this.$t.bind(this));
    },
  },
  methods: {
    money(value) {
      return formatMoney(value, { currency: this.labelOptions.currency, locale: this.labelOptions.locale });
    },
    // «старая → новая»; исключённая или без новой — только старая; без старой — пусто.
    priceText(car) {
      if (car.old_price == null) return "";
      if (car.excluded || car.new_price == null) return this.money(car.old_price);
      return `${this.money(car.old_price)} → ${this.money(car.new_price)}`;
    },
    perDay(car) {
      return Array.isArray(car.days) && car.days.length > 1;
    },
    // «26.10 — исключена», «27.10 — 1 000 → 1 100»: исключённый день — пометка без цен, день без старой цены — только дата.
    dayText(day) {
      const date = formatShortDate(day.date);
      if (day.excluded || day.new_price == null) return `${date} — ${this.$t("dynamic_pricing.rec.day_excluded")}`;
      if (day.old_price == null) return date;
      return `${date} — ${this.money(day.old_price)} → ${this.money(day.new_price)}`;
    },
  },
};
</script>

<style scoped>
.dp-rec__wrap-btn {
  max-width: 100%;
  height: auto !important;
  min-height: 28px;
  white-space: normal;
}
.dp-rec__wrap-btn >>> .v-btn__content {
  flex: 1 1 auto;
  white-space: normal;
  word-break: break-word;
  text-align: left;
  padding: 4px 0;
}
</style>
